// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import {
    addSearchHistory,
    getSearchHistory,
    SearchHistoryItem,
} from "@insite/client-framework/Services/AutocompleteService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import getAutocompleteModel from "@insite/client-framework/Store/CommonHandlers/GetAutocompleteModel";
import sendAutocompleteOdpEvent from "@insite/client-framework/Store/CommonHandlers/SendAutocompleteOdpEvent";
import sendSearchTracking from "@insite/client-framework/Store/CommonHandlers/SendSearchTracking";
import { startSearch } from "@insite/client-framework/Store/CommonHandlers/StartSearch";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import { AutocompleteItemModel, AutocompleteModel } from "@insite/client-framework/Types/ApiModels";
import AutocompleteBrands, {
    AutocompleteBrandsStyles,
} from "@insite/content-library/Widgets/Header/AutocompleteBrands";
import AutocompleteCategories, {
    AutocompleteCategoriesStyles,
} from "@insite/content-library/Widgets/Header/AutocompleteCategories";
import AutocompleteContent, {
    AutocompleteContentStyles,
} from "@insite/content-library/Widgets/Header/AutocompleteContent";
import AutocompleteSuggestions, {
    AutocompleteSuggestionsStyles,
} from "@insite/content-library/Widgets/Header/AutocompleteSuggestions";
import { FormFieldSizeVariant } from "@insite/mobius/FormField";
import { sizeVariantValues } from "@insite/mobius/FormField/formStyles";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Search from "@insite/mobius/Icons/Search";
import {
    ContentBodyProps,
    OverflowWrapperProps,
    PopoverPresentationProps,
    PositionStyle,
} from "@insite/mobius/Popover";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import FancyScroll, { FancyScrollRefProps } from "@stoneage/App/Components/Common/FancyScroll";
import { FloatingTooltipStyles } from "@stoneage/App/Components/FloatingTooltip/FloatingTooltip";
import FloatingMenuTooltip from "@stoneage/App/Components/Menu/FloatingMenuTooltip";
import { CustomAppState } from "@stoneage/App/Store/Reducers";
import TextField, { TextFieldPresentationProps } from "@stoneage/MobiusOverrides/TextField";
import AutocompleteProducts, {
    AutocompleteProductsStyles,
} from "@stoneage/Overrides/Widgets/Header/AutocompleteProducts";
import SearchHistory, { SearchHistoryStyles } from "@stoneage/Overrides/Widgets/Header/SearchHistory";
import breakpointMediaQueries from "@stoneage/XDL/Utilities/breakpointMediaQueries";
import { FloatingOverlay, FloatingPortal } from "@floating-ui/react";
import debounce from "lodash/debounce";
import throttle from "lodash/throttle";
import React, { createRef } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, ThemeProps, withTheme } from "styled-components";
import Icon from "@insite/mobius/Icon";
import IconClear from "@stoneage/XDL/Icons/IconClear";
import { getBillToCustomerNumber, trackAsImpersonation, trackGaSearchEvent } from "@stoneage/App/Utilities/DataLayer";
import { xClassNames } from "@stoneage/XDL/Utilities";

interface OwnProps {
    /** A unique identifier to assist with id-generation for `aria-controls`. */
    readonly id: string;
    inputRef?: React.Ref<HTMLInputElement>;
    autocompletePositionFunction?: (element: React.RefObject<HTMLUListElement | null>) => PositionStyle;
    onBeforeGoToUrl?: () => void;
    extendedStyles?: SearchInputStyles;
    isAnimated?: boolean;
}

const mapStateToProps = (state: CustomAppState) => {
    const {
        searchSettings: { autocompleteEnabled, searchHistoryEnabled, searchHistoryLimit, searchPath },
    } = getSettingsCollection(state);
    return {
        autocompleteEnabled,
        searchHistoryEnabled,
        searchHistoryLimit,
        location: getLocation(state),
        searchPath,
        // XNG-Changes
        isTabletScreen: state.xCommon.isTabletScreen,
        // Track
        billToCustomerNumber: getBillToCustomerNumber(state),
        impersonation: trackAsImpersonation(state),
        searchAutoCompleteEvent: state.xGoogleAnalytics.searchAutoCompleteEvent,
    };
};

const mapDispatchToProps = {
    getAutocompleteModel,
    sendSearchTracking,
    sendAutocompleteOdpEvent,
    startSearch,
};

type Props = OwnProps &
    ThemeProps<BaseTheme> &
    HasHistory &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps>;

interface State {
    query: string;
    focusableAutocompleteItems: AutocompleteItemModel[];
    focusedAutocompleteItem?: AutocompleteItemModel;
    focusedSearchHistoryQuery?: string;
    autoCompleteModel?: AutocompleteModel;
    isLoading: boolean;
    clickedProduct: boolean;
    // XNG-Changes
    inputWidth?: number;
    autoCompleteOpen?: boolean;
    // XNG-Changes: mobile animation
    overlayVisible: boolean;
    triggerRect?: DOMRect;
}

const DOWN_KEY = 40;
const UP_KEY = 38;
const ENTER_KEY = 13;

// XNG-Changes: keep in sync with CSS transition durations in renderMobileOverlay
const OVERLAY_TRANSITION_MS = 300;

export interface SearchInputStyles {
    input?: TextFieldPresentationProps;
    popover?: PopoverPresentationProps;
    popoverWrapper?: OverflowWrapperProps;
    popoverContentBody?: ContentBodyProps;
    autocompleteWrapper?: InjectableCss;
    autocompleteColumnWrapper?: InjectableCss;
    autocompleteCommerceSearchWrapper?: InjectableCss;
    searchHistoryStyles?: SearchHistoryStyles;
    autocompleteSuggestionsStyles?: AutocompleteSuggestionsStyles;
    autocompleteCategoriesStyles?: AutocompleteCategoriesStyles;
    autocompleteBrandsStyles?: AutocompleteBrandsStyles;
    autocompleteContentStyles?: AutocompleteContentStyles;
    autocompleteProductsStyles?: AutocompleteProductsStyles;
    // XNG-Changes
    autocompleteColumnProductWrapper?: InjectableCss;
    inputWrapper?: InjectableCss;
    searchPopUpWrapper?: InjectableCss;
    floatingTooltip?: FloatingTooltipStyles;
    fancyScrollCategories?: InjectableCss;
    fancyScrollProducts?: InjectableCss;
    mobileOverlayTopBar?: InjectableCss;
    mobileOverlayResults?: InjectableCss;
}

export const searchInputStyles: SearchInputStyles = {
    input: {
        sizeVariant: "default",
        iconProps: { src: Search },
        border: "underline",
        cssOverrides: {
            formField: css`
                width: 250px;
            `,
        },
    },
    popover: {
        transitionDuration: "regular",
        toggle: false,
    },
    popoverWrapper: {
        as: "div",
        _height: "auto",
        css: css`
            display: inline-flex;
            justify-content: flex-end;
        `,
    },
    popoverContentBody: {
        as: "div",
        _height: "100%",
    },
    autocompleteWrapper: {
        css: css`
            display: flex;
            flex-direction: row;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            flex-direction: column-reverse;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    autocompleteColumnWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            width: 375px;
            padding: 0 20px 15px 20px;
        `,
    },
    autocompleteCommerceSearchWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            padding: 0 20px 15px 20px;
        `,
    },
};

const styles = searchInputStyles;

class SearchInput extends React.Component<Props, State> {
    private readonly styles: SearchInputStyles;

    // XNG-Changes
    private autocompleteColumnProductParentMenu = React.createRef<HTMLDivElement>();
    private autocompleteColumnCategoryParentMenu = React.createRef<HTMLDivElement>();
    private inputWrapperRef = createRef<HTMLDivElement>();
    private fancyScrollRef = createRef<FancyScrollRefProps>();
    private resizeObserver: ResizeObserver | null = null;
    // XNG-Changes: ref on dummy trigger to capture its position for open animation
    private triggerWrapperRef = createRef<HTMLDivElement>();

    constructor(props: Props) {
        super(props);

        this.styles = mergeToNew(styles, props.extendedStyles);
        const parsedQuery = parseQueryString<{ query?: string }>(this.props.location.search);

        this.state = {
            query: parsedQuery.query || "",
            focusableAutocompleteItems: [],
            isLoading: false,
            clickedProduct: true,
            // XNG-Changes
            autoCompleteOpen: false,
            overlayVisible: false,
        };
    }

    list = React.createRef<HTMLUListElement>();
    popover = React.createRef<any>();
    showAutocomplete = true;

    // XNG-Changes: single source of truth for popup width
    private get popupWidth(): number {
        if (this.props.isTabletScreen) {
            return window.innerWidth;
        }
        return this.state.inputWidth ?? this.inputWrapperRef.current?.getBoundingClientRect().width ?? 300;
    }

    // XNG-Changes: shared TextField props used by both the mobile overlay and desktop inputs,
    // eliminating the duplication that previously existed between renderMobileOverlay and render.
    private get sharedSearchFieldProps() {
        const searchId = `headerSearch-${this.props.id}`;
        return {
            ...this.styles.input,
            ref: this.props.inputRef,
            value: this.state.query,
            onChange: this.handleInputChange,
            onKeyDown: this.handlePopoverKeyDown,
            onFocus: this.handleInputFocus,
            onBlur: this.handleGaSearchEvent,
            placeholder: translate("Search by Keyword"),
            autoComplete: "off" as const,
            clickableText: translate("submit search"),
            id: searchId,
            "aria-multiline": "false" as const,
            "aria-required": "false" as const,
            "data-test-selector": "headerSearchInputTextField",
            className: "search-input",
            maxLength: 100,
            iconClickableProps: {
                className: "searchClickable",
                onClick: () => this.doSearch(),
            },
            clearClickableProps: this.state.query
                ? { clickableProps: { onClick: () => this.setState({ query: "" }) } }
                : undefined,
        };
    }

    componentDidUpdate(prevProps: Props, prevState: State) {
        if (this.props.location.pathname !== prevProps.location.pathname) {
            // clear the search box on navigate away from search
            if (!this.props.location.pathname.toLowerCase().startsWith(`/${this.props.searchPath.toLowerCase()}`)) {
                // TODO ISC-12679 - Redesign so that this can be moved to a field of the class rather than state.
                this.setState({ query: "" });

                // XNG-Changes
                // OOTB issue, when a user do search and return back to home page, the history not showAutocomplete update so the history popup not appear
                this.showAutocomplete = true;
            } else {
                this.closeAutocomplete();
            }
        }
        if (
            this.state.autoCompleteModel &&
            prevState.autoCompleteModel &&
            this.state.autoCompleteModel !== prevState.autoCompleteModel
        ) {
            this.updateFocusableItems();
        }
    }

    componentDidMount() {
        window.addEventListener("resize", this.handleResize, true);
        // XNG-Changes
        this.resizeObserver = new ResizeObserver(entries => {
            for (let entry of entries) {
                this.setState({ inputWidth: entry.contentRect.width });
            }
        });

        if (this.inputWrapperRef.current) {
            this.resizeObserver.observe(this.inputWrapperRef.current);
        }
    }

    componentWillUnmount() {
        window.removeEventListener("resize", this.handleResize);
        // XNG-Changes
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }
    }

    handleResize = () => {
        this.updateFocusableItems();
    };

    updateFocusableItems = () => {
        if (this.state.isLoading || !this.state.autoCompleteModel) {
            return;
        }

        const { categories, brands, content, products } = this.state.autoCompleteModel;
        const isProductsOnTop = window.innerWidth < this.props.theme.breakpoints.values[3];
        const newFocusableOptions = isProductsOnTop
            ? (products || ([] as AutocompleteItemModel[]))
                  .concat(categories || [])
                  .concat(brands || [])
                  .concat(content || [])
            : (categories || [])
                  .concat(brands || [])
                  .concat(content || [])
                  .concat(products || []);
        this.setState({
            focusableAutocompleteItems: newFocusableOptions,
            focusedAutocompleteItem: undefined,
            focusedSearchHistoryQuery: undefined,
        });
    };

    doSearch = () => {
        if (this.state.query.trim() === "") {
            return;
        }

        this.showAutocomplete = false;

        this.props.startSearch({
            query: this.state.query,
            autocompleteModel: this.state.autoCompleteModel,
            searchPath: this.props.searchPath,
            goToUrl: this.goToUrl,
            searchHistoryEnabled: this.props.searchHistoryEnabled,
            searchHistoryLimit: this.props.searchHistoryLimit,
        });
    };

    // XNG-Changes: fix empty popup
    clearSearchQuery = () => {
        this.showAutocomplete = true;
        this.setState({ query: "" });
    };

    // XNG-Change: track GA4
    handleGaSearchEvent = () => {
        if (this.state.autoCompleteModel && this.props.searchAutoCompleteEvent) {
            const { categories, brands, content, products } = this.state.autoCompleteModel;
            const resultCount =
                (products?.length || 0) + (categories?.length || 0) + (content?.length || 0) + (brands?.length || 0);
            trackGaSearchEvent(
                this.state.query,
                resultCount,
                this.props.impersonation,
                this.props.billToCustomerNumber,
            );
        }
    };

    // XNG-Changes
    loadAutocomplete = debounce((query: string) => {
        this.props.getAutocompleteModel({
            query,
            onSuccess: result => {
                if (this.showAutocomplete) {
                    this.setState(
                        {
                            autoCompleteModel: result,
                        },
                        () => {
                            this.fancyScrollRef?.current?.updateScrollable();
                        },
                    );
                    this.updatePopoverVisibility(result);
                }
            },
            onComplete(resultProps) {
                if (resultProps.apiResult) {
                    this.onSuccess?.(resultProps.apiResult);
                }
            },
        });
    }, 200);

    handleInputChange = (event: React.ChangeEvent<HTMLInputElement>, toggleAutoComplete = true) => {
        this.showAutocomplete = toggleAutoComplete;
        const newQuery = event.target.value;
        this.setState({ query: newQuery });
        // XNG-Changes
        if (this.state.query) {
            this.loadAutocomplete(newQuery);
        }

        // XNG-Changes
        this.updatePopoverVisibility(this.state.autoCompleteModel);
        if (newQuery === "") {
            this.clearSearchQuery();
        }
    };

    // XNG-Changes: on mobile, capture trigger position then animate in via double rAF
    openAutocomplete = () => {
        if (this.props.isTabletScreen) {
            const triggerRect = this.triggerWrapperRef.current?.getBoundingClientRect() as DOMRect | undefined;
            // Mount overlay with overlayVisible=false (start state: bar at header position)
            this.setState({ autoCompleteOpen: true, overlayVisible: false, triggerRect }, () => {
                // Two rAFs ensure the browser has painted before we flip to end state,
                // which is what actually kicks off the CSS transition
                requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                        this.setState({ overlayVisible: true });
                    });
                });
            });
        } else {
            this.setState({ autoCompleteOpen: true });
        }
    };

    // XNG-Changes: on mobile, reverse animation — FloatingOverlay's onTransitionEnd unmounts cleanly
    closeAutocomplete = () => {
        if (this.props.isTabletScreen) {
            // Just flip overlayVisible; the FloatingOverlay onTransitionEnd handles the unmount
            this.setState({ overlayVisible: false });
        } else {
            this.setState({ autoCompleteOpen: false });
        }
    };

    // XNG-Changes
    isPopoverVisible(results: AutocompleteModel | undefined) {
        let isVisible: boolean | null = false;
        if (this.state.query && this.state.query.length > 2 && results) {
            const { categories, brands, content, products, suggestions, popularBrands, popularCategories } = results;
            const hasContent = this.state.autoCompleteModel?.isRetailSearchCompletionResults
                ? (categories && categories.length > 0) ||
                  (brands && brands.length > 0) ||
                  (suggestions && suggestions.length > 0) ||
                  (popularBrands && popularBrands.length > 0) ||
                  (popularCategories && popularCategories.length > 0)
                : (categories && categories.length > 0) ||
                  (brands && brands.length > 0) ||
                  (content && content.length > 0);

            isVisible = this.props.autocompleteEnabled && hasContent;
        } else if (!this.state.query) {
            const searchHistoryItems = getSearchHistory();
            isVisible = this.props.searchHistoryEnabled && searchHistoryItems && searchHistoryItems.length > 0;
        }
        return isVisible;
    }

    // XNG-Changes
    updatePopoverVisibility(results: AutocompleteModel | undefined) {
        // XNG-Changes: on mobile the overlay stays open until Cancel or search navigation.
        // Results show/hide inside it based on query/autoCompleteModel state — never auto-close.
        if (this.props.isTabletScreen) {
            return;
        }

        if (this.isPopoverVisible(results)) {
            this.openAutocomplete();
        } else {
            this.closeAutocomplete();
        }
    }

    // XNG-Changes
    handleInputFocus = () => {
        this.updatePopoverVisibility(this.state.autoCompleteModel);
    };

    // XNG-Changes
    clearSearchHistoryCallback = () => {
        this.updatePopoverVisibility(undefined);
    };

    getPopoverPosition = (element: React.RefObject<HTMLUListElement | null>) => {
        if (this.props.autocompletePositionFunction) {
            return this.props.autocompletePositionFunction(element);
        }

        const positionStyle: PositionStyle = {
            position: "fixed",
        };

        if (element.current) {
            const rect = element.current.getBoundingClientRect();
            positionStyle.top =
                rect.top +
                (rect.height > 100
                    ? sizeVariantValues[this.styles?.input?.sizeVariant || ("default" as FormFieldSizeVariant)].height
                    : rect.height);
            positionStyle.right = document.documentElement.clientWidth - rect.right;

            if (this.props.isAnimated) {
                // Adjust for compact header usage
                positionStyle.top += 3;
                if (this.props.theme.header.isBorder && this.props.theme.header.isBorderRight) {
                    positionStyle.right -= Number(this.props.theme.header.borderThickness);
                }
                if (this.props.theme.header.isBorder && this.props.theme.header.isBorderTop) {
                    positionStyle.top -= Number(this.props.theme.header.borderThickness);
                }
            }
        }

        return positionStyle;
    };

    moveFocus = throttle((direction: "prev" | "next") => {
        if (this.state.query) {
            this.setState(({ focusedAutocompleteItem, focusableAutocompleteItems }) => {
                const focusableQueue =
                    direction === "prev" ? focusableAutocompleteItems.slice().reverse() : focusableAutocompleteItems;
                const focusedIndex = focusableQueue.indexOf(focusedAutocompleteItem as AutocompleteItemModel);
                const newFocusedOption = focusableQueue.find((_, index) => index > focusedIndex);
                return { focusedAutocompleteItem: newFocusedOption };
            });
        } else {
            this.setState(({ focusedSearchHistoryQuery }) => {
                const searchHistoryItems = getSearchHistory();
                const focusableQueue = direction === "prev" ? searchHistoryItems.slice().reverse() : searchHistoryItems;
                const focusedSearchHistoryItem = focusableQueue.find(o => o.query === focusedSearchHistoryQuery);
                const focusedIndex = focusableQueue.indexOf(focusedSearchHistoryItem as SearchHistoryItem);
                const newFocusedOption = focusableQueue.find((_, index) => index > focusedIndex);
                return { focusedSearchHistoryQuery: newFocusedOption?.query };
            });
        }
    }, 100);

    handlePopoverKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        switch (event.keyCode) {
            case DOWN_KEY:
                this.moveFocus("next");
                break;
            case UP_KEY:
                this.moveFocus("prev");
                break;
            case ENTER_KEY:
                if (this.state.focusedAutocompleteItem) {
                    // XNG-Changes
                    (event.target as any).blur();
                    this.goToUrl(this.state.focusedAutocompleteItem.url);
                } else if (this.state.focusedSearchHistoryQuery) {
                    // XNG-Changes
                    (event.target as any).blur();
                    this.setState({ query: this.state.focusedSearchHistoryQuery }, () => {
                        this.doSearch();
                    });
                } else {
                    // XNG-Changes
                    (event.target as any).blur();
                    this.doSearch();
                }

                break;
            default:
                break;
        }
    };

    handlePopoverClose = () => {
        if (!this.state.clickedProduct && (this.state.autoCompleteModel?.products?.length ?? 0) > 0) {
            this.props.sendAutocompleteOdpEvent({});
        }

        this.setState({
            clickedProduct: false,
        });

        this.setState({
            focusedAutocompleteItem: undefined,
            focusedSearchHistoryQuery: undefined,
        });
    };

    trackAutcompleteSearchResults = () => {
        // Make sure only to trigger for autocomplete results
        if (!this.state.query || !this.props.autocompleteEnabled || !this.state.autoCompleteModel) {
            return;
        }

        const { categories, brands, content, products } = this.state.autoCompleteModel;
        const resultCount =
            (products?.length ?? 0) + (categories?.length || 0) + (content?.length || 0) + (brands?.length || 0);
        if (resultCount > 0) {
            // Track an autocomplete of 1+ items
            this.props.sendSearchTracking({
                searchEvent: "autocompleteSearchResults",
                searchTerm: this.state.query,
                resultCount,
                productCount: products?.length,
                categoryCount: categories?.length,
                contentCount: content?.length,
                brandCount: brands?.length,
            });
            return;
        }

        // Track zero resultCount search results and triggers from this.doSearch
        this.props.sendSearchTracking({
            searchEvent: "searchResults",
            searchTerm: this.state.query,
            resultCount,
        });
    };

    goToUrl = (url: string, track = true, autoCompleteResultLength?: number) => {
        const data = {
            searchQuery: this.state.query,
            searchPosition:
                this.state.autoCompleteModel?.suggestions?.findIndex(o => o.url === url) ??
                this.state.autoCompleteModel?.products?.findIndex(o => o.url === url) ??
                -1,
            attributionToken: this.state.autoCompleteModel?.attributionToken,
        };

        if (data.searchPosition >= 0) {
            this.props.sendAutocompleteOdpEvent(data);
            this.setState({ clickedProduct: true });
        }

        this.props.onBeforeGoToUrl && this.props.onBeforeGoToUrl();
        if (track) {
            this.trackAutcompleteSearchResults();
        }
        this.closeAutocomplete();
        // XNG-Changes: only when clicking/hitting enter on pdp product from search result
        //  (having 1 product search result only) remove stale autocomplete results
        if (autoCompleteResultLength && autoCompleteResultLength === 1) {
            this.setState({
                // don't use stale results on next search and go to wrong PDP
                autoCompleteModel: undefined,
            });
        }
        this.props.history.push(url);
    };

    // XNG-Changes
    renderAutocomplete = () => {
        if (!this.props.autocompleteEnabled) {
            return null;
        }

        if (!this.state.autoCompleteModel) {
            return null;
        }

        const {
            categories,
            brands,
            content,
            products,
            suggestions,
            popularBrands,
            popularCategories,
            isRetailSearchCompletionResults,
        } = this.state.autoCompleteModel;

        // XNG-Changes
        const hasProducts = products && products.length > 0;
        const hasContent = isRetailSearchCompletionResults
            ? (categories && categories.length > 0) ||
              (brands && brands.length > 0) ||
              (suggestions && suggestions.length > 0) ||
              (popularBrands && popularBrands.length > 0) ||
              (popularCategories && popularCategories.length > 0)
            : (categories && categories.length > 0) || (brands && brands.length > 0) || (content && content.length > 0);

        return (
            <>
                {isRetailSearchCompletionResults &&
                    ((suggestions && suggestions.length > 0) ||
                        (categories && categories.length > 0) ||
                        (popularCategories && popularCategories.length > 0) ||
                        (brands && brands.length > 0) ||
                        (popularBrands && popularBrands.length > 0)) && (
                        <StyledWrapper
                            {...this.styles.autocompleteCommerceSearchWrapper}
                            className="autocomplete-commerce-search-wrapper"
                        >
                            {suggestions && suggestions.length > 0 && (
                                <AutocompleteSuggestions
                                    suggestions={suggestions}
                                    focusedItem={this.state.focusedAutocompleteItem}
                                    goToUrl={this.goToUrl}
                                    extendedStyles={this.styles.autocompleteSuggestionsStyles}
                                />
                            )}
                            {((categories && categories.length > 0) ||
                                (popularCategories && popularCategories.length > 0)) && (
                                <AutocompleteCategories
                                    categories={categories}
                                    popularCategories={popularCategories}
                                    focusedItem={this.state.focusedAutocompleteItem}
                                    goToUrl={this.goToUrl}
                                    extendedStyles={this.styles.autocompleteCategoriesStyles}
                                    isRetailSearchCompletionResults={isRetailSearchCompletionResults}
                                />
                            )}
                            {((brands && brands.length > 0) || (popularBrands && popularBrands.length > 0)) && (
                                <AutocompleteBrands
                                    brands={brands}
                                    popularBrands={popularBrands}
                                    focusedItem={this.state.focusedAutocompleteItem}
                                    goToUrl={this.goToUrl}
                                    extendedStyles={this.styles.autocompleteBrandsStyles}
                                    isRetailSearchCompletionResults={isRetailSearchCompletionResults}
                                />
                            )}
                        </StyledWrapper>
                    )}

                {!isRetailSearchCompletionResults && (
                    <StyledWrapper
                        {...this.styles.autocompleteWrapper}
                        // XNG-Changes: use popupWidth for correct width on mobile/tablet
                        style={{
                            width: `${this.popupWidth}px`,
                        }}
                        className="autocomplete-wrapper"
                    >
                        {/* XNG-Changes */}
                        {((categories && categories.length > 0) ||
                            (brands && brands.length > 0) ||
                            (content && content.length > 0)) && (
                            <FancyScroll
                                refElement={this.autocompleteColumnCategoryParentMenu}
                                ref={this.fancyScrollRef}
                                extendedStyles={this.styles.fancyScrollCategories}
                            >
                                <StyledWrapper
                                    {...this.styles.autocompleteColumnWrapper}
                                    className={xClassNames.append(
                                        !!hasProducts && "show-divider",
                                        "autocomplete-column-wrapper",
                                    )}
                                    style={{
                                        maxWidth: this.props.isTabletScreen ? "100%px" : `${this.popupWidth / 2}px`,
                                    }}
                                    ref={this.autocompleteColumnCategoryParentMenu}
                                >
                                    <AutocompleteCategories
                                        categories={categories}
                                        focusedItem={this.state.focusedAutocompleteItem}
                                        goToUrl={this.goToUrl}
                                        extendedStyles={this.styles.autocompleteCategoriesStyles}
                                    />
                                    <AutocompleteBrands
                                        brands={brands}
                                        focusedItem={this.state.focusedAutocompleteItem}
                                        goToUrl={this.goToUrl}
                                        extendedStyles={this.styles.autocompleteBrandsStyles}
                                    />
                                    <AutocompleteContent
                                        content={content}
                                        focusedItem={this.state.focusedAutocompleteItem}
                                        goToUrl={this.goToUrl}
                                        extendedStyles={this.styles.autocompleteContentStyles}
                                    />
                                </StyledWrapper>
                            </FancyScroll>
                        )}
                        {hasProducts && (
                            <FancyScroll
                                refElement={this.autocompleteColumnProductParentMenu}
                                ref={this.fancyScrollRef}
                                extendedStyles={this.styles.fancyScrollProducts}
                            >
                                <StyledWrapper
                                    {...this.styles.autocompleteColumnProductWrapper}
                                    className="autocomplete-column-wrapper"
                                    ref={this.autocompleteColumnProductParentMenu}
                                    // XNG-Changes
                                    {...{ hasContent }}
                                >
                                    <AutocompleteProducts
                                        products={products}
                                        focusedItem={this.state.focusedAutocompleteItem}
                                        goToUrl={this.goToUrl}
                                        extendedStyles={this.styles.autocompleteProductsStyles}
                                        // XNG-Changes
                                        {...{ doSearch: this.doSearch }}
                                    />
                                </StyledWrapper>
                            </FancyScroll>
                        )}
                    </StyledWrapper>
                )}
            </>
        );
    };

    // XNG-Changes
    renderSearchAutoCompleteContent = () => {
        const searchHistoryItems = getSearchHistory();
        const hasSearchHistory = this.props.searchHistoryEnabled && searchHistoryItems && searchHistoryItems.length > 0;
        // XNG-Changes: add hasSearchHistory
        if (!this.state.query && this.showAutocomplete && hasSearchHistory) {
            return (
                <StyledWrapper
                    {...styles.searchPopUpWrapper}
                    style={{
                        width: `${this.popupWidth}px`,
                    }}
                >
                    <SearchHistory
                        focusedQuery={this.state.focusedSearchHistoryQuery}
                        goToUrl={this.goToUrl}
                        clearSearchHistoryCallback={this.clearSearchHistoryCallback}
                    />
                </StyledWrapper>
            );
        }
        if (this.state.query) {
            return this.renderAutocomplete();
        }
    };

    // XNG-Changes: mobile overlay using Floating UI primitives (same stack as FloatingMenuTooltip).
    renderMobileOverlay = () => {
        const { styles } = this;
        const searchId = `headerSearch-${this.props.id}`;
        const { overlayVisible, triggerRect } = this.state;

        const topBarStartTop = triggerRect?.top ?? 0;
        const transition = `all ${OVERLAY_TRANSITION_MS}ms ease`;

        return (
            <FloatingPortal>
                <FloatingOverlay
                    lockScroll
                    style={{
                        position: "fixed",
                        inset: 0,
                        zIndex: 9998,
                        background: "white",
                        opacity: overlayVisible ? 1 : 0,
                        transition,
                    }}
                    onTransitionEnd={() => {
                        // Only unmount after the closing transition (overlayVisible=false) finishes
                        if (!this.state.overlayVisible) {
                            this.setState({ autoCompleteOpen: false });
                        }
                    }}
                >
                    <StyledWrapper
                        {...styles.mobileOverlayTopBar}
                        style={{
                            top: overlayVisible ? 0 : topBarStartTop,
                            transition,
                        }}
                    >
                        <Icon
                            src={IconClear}
                            color="secondary"
                            size={18}
                            onClick={() => {
                                this.closeAutocomplete();
                                this.clearSearchQuery();
                            }}
                        />
                        <StyledWrapper ref={this.inputWrapperRef} {...styles.inputWrapper} style={{ flex: 1 }}>
                            {/* XNG-Changes: spread sharedSearchFieldProps; only autoFocus is mobile-overlay-specific */}
                            <TextField {...this.sharedSearchFieldProps} autoFocus={overlayVisible} />
                        </StyledWrapper>
                    </StyledWrapper>

                    <StyledWrapper
                        {...styles.mobileOverlayResults}
                        style={{
                            opacity: overlayVisible ? 1 : 0,
                            transition: overlayVisible
                                ? `opacity ${OVERLAY_TRANSITION_MS}ms ease ${OVERLAY_TRANSITION_MS / 2}ms`
                                : `opacity ${OVERLAY_TRANSITION_MS / 2}ms ease`,
                            pointerEvents: overlayVisible ? "auto" : "none",
                        }}
                    >
                        {this.renderSearchAutoCompleteContent()}
                    </StyledWrapper>
                </FloatingOverlay>
            </FloatingPortal>
        );
    };

    // XNG-Changes
    render() {
        const { styles } = this;
        const searchId = `headerSearch-${this.props.id}`;
        const { isTabletScreen } = this.props;

        if (isTabletScreen) {
            return (
                <>
                    <VisuallyHidden as="label" htmlFor={searchId} id={`${searchId}-label`}>
                        {translate("Site Search")}
                    </VisuallyHidden>

                    {/* Dummy read-only trigger — intentionally not using sharedSearchFieldProps
                        since this is a non-interactive placeholder with its own distinct role */}
                    <StyledWrapper
                        ref={this.triggerWrapperRef}
                        {...styles.inputWrapper}
                        onClick={this.openAutocomplete}
                    >
                        <TextField
                            {...styles.input}
                            value={this.state.query}
                            placeholder={translate("Search by Keyword")}
                            id={searchId}
                            readOnly
                            tabIndex={-1}
                            className="search-input"
                            onChange={() => {}}
                        />
                    </StyledWrapper>

                    {/* Floating UI overlay — only mounted while open */}
                    {this.state.autoCompleteOpen && this.renderMobileOverlay()}
                </>
            );
        }

        const searchInput = (
            <StyledWrapper ref={this.inputWrapperRef} {...styles.inputWrapper}>
                {/* XNG-Changes: spread sharedSearchFieldProps; only isAnimated is desktop-specific */}
                <TextField {...this.sharedSearchFieldProps} isAnimated={this.props.isAnimated} />
            </StyledWrapper>
        );

        return (
            <>
                <VisuallyHidden as="label" htmlFor={searchId} id={`${searchId}-label`}>
                    {translate("Site Search")}
                </VisuallyHidden>
                <FloatingMenuTooltip
                    trigger="manual"
                    isOpen={this.state.autoCompleteOpen}
                    triggerComponent={searchInput}
                    overlay={true}
                    showArrow={false}
                    placement="bottom-start"
                    offset={{ mainAxis: 1 }}
                    extendedStyles={styles.floatingTooltip}
                    onOpenChange={open => this.setState({ autoCompleteOpen: open })}
                >
                    {this.renderSearchAutoCompleteContent()}
                </FloatingMenuTooltip>
            </>
        );
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(withHistory(withTheme(SearchInput)));
